import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppState } from "../../contexts/App";
import { PreviewHint, PreviewSiteContext } from "../../contexts/PreviewSite";
import { useKeyPress } from "../../hooks/KeyPress";
import { usePreviousPathTracker } from "../../hooks/PreviousPath";
import { useQueryUrl } from "../../hooks/QueryUrl";
import { toLegacyPath } from "../../hooks/SitePath";
import SymbolKeepoIcon from "../../icons/SymbolKeepo";
import { homeTitleFor, pageAddress, pageIdForPath, pageView } from "../../model/pages";
import { Site } from "../../model/site";
import { BrazilFlag } from "../../pages/Account/icons/Flags";
import { api } from "../../services/api";
import { releasesStatus, useReleasesStatus } from "../../services/releases";
import { stopScroll } from "../../utils/modal";
import { classNames } from "../../utils/styles";
import { LivePreview } from "../LivePreview";
import { MobilePreview } from "../MobilePreview";
import { PlusPromo } from "../PlusPromo";
import ReleaseAnnouncement from "../ReleaseAnnouncement";
import { ToastProvider } from "../Toast";
import {
  BottomMoreBackdrop,
  BottomMoreDivider,
  BottomMoreHeader,
  BottomMoreSheet,
  BottomNav,
  BottomNavItem,
  Greeting,
  Logo,
  MadeInBrazil,
  Main,
  NavGroup,
  NavGroupTitle,
  NavItem,
  NewDot,
  PreviewAside,
  Shell,
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarPlusPromo,
} from "./styles";

// Routes rendered without the panel chrome (auth flows, public pages).
const ROUTES_WITHOUT_SHELL = ["/register", "/comecar", "/recovery-password", "/recover-password", "/legal"];

// Top-level screens where the mobile bottom nav is shown. Inner screens (item editing,
// settings sub-pages, checkout) keep their own back button and fixed save bars instead.
const ROUTES_WITH_BOTTOM_NAV = [
  "/sites",
  "/domains",
  "/domains/mine",
  "/account",
  "/help",
  "/feedback",
  "/analytics",
  "/contacts",
  "/settings",
  "/keepo",
  "/editor/pages",
  "/editor/sections",
  "/subscription/features",
  "/business-card",
  "/links",
  "/novidades",
];

// Screens reachable from the desktop sidebar, where the page header's back button is redundant.
export const DESKTOP_ROOT_ROUTES = [
  "/sites",
  "/domains",
  "/domains/mine",
  "/account",
  "/help",
  "/feedback",
  "/keepo",
  "/links",
  "/novidades",
  "/subscription/features",
  "/subscription/list",
  "/editor/title",
];

// The subscription screens that can be a step of creating or editing a Keepo
const SUBSCRIPTION_FLOW_ROUTES = ["/subscription/features", "/subscription/checkout"];

// Editor screens that already show the Keepo full-screen, so the side preview is redundant.
const EDITOR_ROUTES_WITHOUT_PREVIEW = ["/editor/publish/success"];
// First steps of the Keepo creation (naming it, how to start, telling the AI about the business): no preview yet
// (neither the side one nor the phone's button). Leaving them to start building, the side preview slides in.
const CREATION_ROUTES_WITHOUT_SIDE_PREVIEW = ["/editor/title", "/editor/start", "/editor/ai"];

// The screen the layout decides by: without the trailing slash, and a Keepo's address (/sites/<id>/settings) as its
// old screen (/settings), so the lists above hold for both
export const normalizePath = (path: string) => toLegacyPath(path.length > 1 ? path.replace(/\/+$/, "") : path);

const strokeIconProps = {
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const KeeposIcon = () => (
  <svg {...strokeIconProps}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

const DomainsIcon = () => (
  <svg {...strokeIconProps}>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

// A card with a person and the lines of their contact
const BusinessCardIcon = () => (
  <svg {...strokeIconProps}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <circle cx="8" cy="11" r="2" />
    <path d="M5 16a3 3 0 0 1 6 0M14 10h5M14 14h3" />
  </svg>
);

// Two links of a chain: Keepo Links. Drawn edge to edge, so shrunk to the area of the other icons (3 to 21), with
// a thicker stroke that comes out at their same 2px
const LINK_ICON_SCALE = 0.88;
const LinkIcon = () => (
  <svg {...strokeIconProps}>
    <g transform={`translate(12 12) scale(${LINK_ICON_SCALE}) translate(-12 -12)`} strokeWidth={2 / LINK_ICON_SCALE}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </g>
  </svg>
);

// Three lines: the menu of the phone's bar that holds the rest of the screens
const MenuIcon = () => (
  <svg {...strokeIconProps}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloseMenuIcon = () => (
  <svg {...strokeIconProps}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const CreateIcon = () => (
  <svg {...strokeIconProps}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v8" />
    <path d="M8 12h8" />
  </svg>
);

const PlusIcon = () => (
  <svg {...strokeIconProps}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const AccountIcon = () => (
  <svg {...strokeIconProps}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
  </svg>
);

const HelpIcon = () => (
  <svg {...strokeIconProps}>
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </svg>
);

// A megaphone: what Keepo launched
const ReleasesIcon = () => (
  <svg {...strokeIconProps}>
    <path d="M3 11l18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

const FeedbackIcon = () => (
  <svg {...strokeIconProps}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

type NavEntry = {
  key: string;
  label: string;
  /** Shorter label for the mobile bottom nav, where space is tight. */
  shortLabel?: string;
  icon: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  className?: string;
  /** Something new to see there: a dot on the icon */
  dot?: boolean;
  /** Sidebar only: entries shown indented under this one (e.g. the Domains screens). */
  children?: NavEntry[];
};

type Props = {
  authenticated: boolean;
  site?: Site;
  children: React.ReactNode;
};

export const AppShell = ({ authenticated, site, children }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { appState } = useAppState();
  const pathname = normalizePath(location.pathname);
  usePreviousPathTracker();
  const [userName, setUserName] = useState<string>();
  // the account's language: the published page's texts (the menu's home) follow it, not the editor's
  const [accountLang, setAccountLang] = useState<string>();
  // Unsaved version of the site published by the editor screen being edited (if any)
  const [previewSite, setPreviewSite] = useState<Site>();
  const [isPreviewLoading, setPreviewLoading] = useState(false);
  const [previewHint, setPreviewHint] = useState<PreviewHint>();
  const [previewAddress, setPreviewAddress] = useState<string>();
  const previewSiteContext = useMemo(
    () => ({ setPreviewSite, setPreviewLoading, setPreviewHint, setPreviewAddress }),
    []
  );

  // The preview shows the page of the Keepo being edited (?page), with its menu, as the published page would.
  // From the URL (not useCurrentPage): the shell is outside the site's context.
  // With its pages on any plan: a FREE Keepo builds them too (publishing it as FREE leaves them off).
  const editingPageId = useQueryUrl().get("page");
  const shownSite = previewSite || site;
  // A page picked in the preview itself (its menu): shown only while nothing else changes, so editing, or going to
  // another screen or page, brings back the page being edited
  const [browsed, setBrowsed] = useState<{
    pageId: string | null;
    site?: Site;
    pathname: string;
    editing: string | null;
  }>();
  const browsing =
    browsed && browsed.site === shownSite && browsed.pathname === pathname && browsed.editing === editingPageId;
  const previewPageId = browsing ? browsed.pageId : editingPageId;
  const previewPage = useMemo(
    () => shownSite && pageView(shownSite, previewPageId, true, homeTitleFor(accountLang)),
    [shownSite, previewPageId, accountLang]
  );
  // the address bar follows the page shown: its own address under the Keepo's
  const shownAddress =
    previewAddress && shownSite ? pageAddress(previewAddress, shownSite, true, previewPageId) : previewAddress;
  const openPreviewPage = useCallback(
    (path: string) => {
      const pageId = shownSite && pageIdForPath(shownSite, true, path);
      if (pageId !== undefined) {
        setBrowsed({ pageId, site: shownSite, pathname, editing: editingPageId });
      }
    },
    [shownSite, pathname, editingPageId]
  );

  useEffect(() => {
    if (!authenticated) {
      return;
    }
    // getUser is cached by the api service, so this doesn't trigger an extra request
    api
      .getUser()
      .then(user => {
        setUserName(user.givenName);
        setAccountLang(user.lang);
      })
      .catch(() => undefined);
  }, [authenticated]);

  // Novidades: the menu's dot and the launch to announce, asked once per login
  const releases = useReleasesStatus();
  useEffect(() => {
    if (authenticated) {
      releasesStatus.load(i18n.language);
    } else {
      releasesStatus.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per login, in the language the editor opened in
  }, [authenticated]);

  // Whether the side preview just arrived from the creation steps (it then slides in). Set before
  // paint, so it never shows in place first. Declared before the early return below so the hook
  // order stays the same when the shell toggles (e.g. "/" -> "/sites" after the token login).
  const lastPathname = useRef(pathname);
  const [isPreviewArriving, setPreviewArriving] = useState(false);
  useLayoutEffect(() => {
    if (lastPathname.current !== pathname) {
      setPreviewArriving(CREATION_ROUTES_WITHOUT_SIDE_PREVIEW.includes(lastPathname.current));
      lastPathname.current = pathname;
    }
  }, [pathname]);

  // The phone's "More" panel (the tools and the account, which don't fit in the bar). Before the early return
  // below, so the hook order stays the same
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButton = useRef<HTMLButtonElement>(null);
  const moreSheet = useRef<HTMLElement>(null);
  const wasMoreOpen = useRef(false);
  useKeyPress("Escape", () => setMoreOpen(false));
  useEffect(() => setMoreOpen(false), [pathname]);
  useEffect(() => (moreOpen ? stopScroll() : undefined), [moreOpen]);
  // Focus goes into the panel when it opens and back to its button when it closes
  useEffect(() => {
    if (moreOpen) {
      moreSheet.current?.querySelector("button")?.focus();
    } else if (wasMoreOpen.current) {
      moreButton.current?.focus();
    }
    wasMoreOpen.current = moreOpen;
  }, [moreOpen]);

  const withShell =
    authenticated && pathname !== "/" && !ROUTES_WITHOUT_SHELL.some(route => pathname.startsWith(route));

  // Toasts on every screen: with the shell, inside it
  if (!withShell) {
    return <ToastProvider>{children}</ToastProvider>;
  }

  const isActive = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);

  const keeposEntry: NavEntry = {
    key: "keepos",
    label: t("components.appShell.nav.keepos"),
    shortLabel: t("components.appShell.nav.short.keepos"),
    icon: <KeeposIcon />,
    onClick: () => navigate("/sites"),
    active: isActive("/sites"),
  };

  const accountEntry: NavEntry = {
    key: "account",
    label: t("components.appShell.nav.account"),
    icon: <AccountIcon />,
    onClick: () => navigate("/account"),
    active: isActive("/account"),
  };

  // Screens inside a Keepo (overview, editor, analytics, settings, its subscription) are reached
  // from its dashboard, so "My Keepos" stays highlighted in the sidebar while there.
  const insideKeepo = [
    "/keepo",
    "/editor",
    "/analytics",
    "/contacts",
    "/settings",
    "/seo",
    "/subscription/detail",
  ].some(isActive);

  const createEntry: NavEntry = {
    key: "create",
    label: t("pages.sites.actions.createNew.button.title"),
    shortLabel: t("components.appShell.nav.short.create"),
    icon: <CreateIcon />,
    // The Sites page owns the creation flow (free limit, resumable drafts, PLUS
    // availability), so it is triggered there through the navigation state.
    onClick: () => navigate("/sites", { state: { createNew: true } }),
  };

  const globalEntries: NavEntry[] = [
    { ...keeposEntry, active: keeposEntry.active || insideKeepo },
    {
      key: "domains",
      label: t("components.appShell.nav.domains"),
      icon: <DomainsIcon />,
      onClick: () => navigate("/domains"),
      active: isActive("/domains"),
      children: [
        {
          key: "domains-register",
          label: t("components.appShell.nav.domainsRegister"),
          icon: null,
          onClick: () => navigate("/domains"),
          active: pathname === "/domains" || isActive("/domains/checkout"),
        },
        {
          key: "domains-mine",
          label: t("components.appShell.nav.domainsMine"),
          icon: null,
          onClick: () => navigate("/domains/mine"),
          // A domain's screen is reached from the list
          active: isActive("/domains") && pathname !== "/domains" && !isActive("/domains/checkout"),
        },
      ],
    },
  ];

  // Creating a Keepo: its first steps, the editor of one never published, and the PLUS screens as a step of
  // the creation (the same "not published yet" the publish screen goes by)
  const isCreatingKeepo =
    CREATION_ROUTES_WITHOUT_SIDE_PREVIEW.includes(pathname) ||
    (isActive("/editor") && !appState.published) ||
    (new URLSearchParams(location.search).get("flow") === "create" && SUBSCRIPTION_FLOW_ROUTES.some(isActive));

  // The sidebar opens Keepos with its screens (the list, creating one) while on them, as it does Domains
  const keeposGroupEntry: NavEntry = {
    key: "keepos-group",
    label: t("components.appShell.nav.keeposGroup"),
    icon: <KeeposIcon />,
    onClick: () => navigate("/sites"),
    active: keeposEntry.active || insideKeepo || isCreatingKeepo,
    children: [
      { ...keeposEntry, icon: null, active: (keeposEntry.active || insideKeepo) && !isCreatingKeepo },
      { ...createEntry, icon: null, active: isCreatingKeepo },
    ],
  };
  const domainsEntry = globalEntries.find(entry => entry.key === "domains") as NavEntry;

  // The tools, in their own group of the sidebar (and the card in the phone's bar). The card of a Keepo is
  // picked on its screen (a person with more than one Keepo has one card each)
  const businessCardEntry: NavEntry = {
    key: "business-card",
    label: t("components.appShell.nav.businessCard"),
    icon: <BusinessCardIcon />,
    onClick: () => navigate("/business-card"),
    active: isActive("/business-card"),
  };
  const linksEntry: NavEntry = {
    key: "links",
    label: t("components.appShell.nav.links"),
    icon: <LinkIcon />,
    onClick: () => navigate("/links"),
    active: isActive("/links"),
  };
  const toolEntries: NavEntry[] = [businessCardEntry, linksEntry];

  const plusEntry: NavEntry = {
    key: "plus",
    label: t("components.appShell.nav.plus"),
    shortLabel: t("components.appShell.nav.short.plus"),
    icon: <PlusIcon />,
    onClick: () => navigate("/subscription/features"),
    active: isActive("/subscription") && !isActive("/subscription/detail"),
    className: "plus",
  };

  const footerEntries: NavEntry[] = [
    {
      key: "releases",
      label: t("components.appShell.nav.releases"),
      icon: <ReleasesIcon />,
      onClick: () => navigate("/novidades"),
      active: isActive("/novidades"),
      dot: releases.unseen > 0 && !isActive("/novidades"),
    },
    {
      key: "help",
      label: t("components.appShell.nav.help"),
      icon: <HelpIcon />,
      onClick: () => navigate("/help"),
      active: isActive("/help"),
    },
    {
      key: "feedback",
      label: t("components.appShell.nav.feedback"),
      icon: <FeedbackIcon />,
      onClick: () => navigate("/feedback"),
      active: isActive("/feedback"),
    },
    accountEntry,
  ];

  // Mobile mirrors the sidebar's macro navigation; Keepo-specific screens live in its dashboard.
  // What doesn't fit in the bar (the tools, the account, help) is in its "More" panel, from the same entries
  // as the sidebar: a new tool shows up in both
  const bottomEntries = [...globalEntries, createEntry, plusEntry];
  const moreEntries = [...toolEntries, ...footerEntries];
  const moreActive = moreEntries.some(entry => entry.active);
  // something new inside More: the dot on its button too (not while open: the entry shows it)
  const moreHasNew = !moreOpen && moreEntries.some(entry => entry.dot);
  // Subscribing while creating or editing a Keepo (`?flow=`): the PLUS and payment screens are steps of
  // that flow, with its preview and without the bottom nav. Opened from anywhere else, they're not
  const flow = new URLSearchParams(location.search).get("flow");
  const isSubscriptionInFlow = (flow === "create" || flow === "edit") && SUBSCRIPTION_FLOW_ROUTES.some(isActive);
  const withBottomNav = ROUTES_WITH_BOTTOM_NAV.includes(pathname) && !isSubscriptionInFlow;
  const withPreview =
    !!site &&
    (isActive("/keepo") || isActive("/editor") || isSubscriptionInFlow) &&
    !EDITOR_ROUTES_WITHOUT_PREVIEW.some(route => pathname.startsWith(route));

  const withSidePreview = withPreview && !CREATION_ROUTES_WITHOUT_SIDE_PREVIEW.includes(pathname);

  // A titled group of the sidebar (the tools): its title over its items
  const renderNavGroup = (key: string, title: string, entries: NavEntry[]) => (
    // a div: a nav here would lose its landmark role to the group
    <NavGroup as="div" key={key} role="group" aria-labelledby={`nav-${key}`}>
      <NavGroupTitle id={`nav-${key}`}>{title}</NavGroupTitle>
      {entries.map(renderNavItem)}
    </NavGroup>
  );

  // The entry's icon, with the dot when there's something new there (screen readers hear it after the label)
  const renderIcon = (icon: React.ReactNode, dot?: boolean, className?: string) => (
    <span className={classNames("relative", { [className ?? ""]: !!className })}>
      {icon}
      {dot && <NewDot aria-hidden="true" />}
    </span>
  );
  const newContent = ` (${t("components.appShell.nav.newContent")})`;
  const renderNewContent = (dot?: boolean) => dot && <span className="sr-only">{newContent}</span>;

  const renderNavItem = ({ key, label, icon, onClick, active, className, dot, children }: NavEntry) =>
    children ? (
      // A group: the parent only stands out by its text, the screen open is marked among its subitems
      <div key={key} className="flex flex-col gap-1">
        <NavItem type="button" onClick={onClick} $parentActive={active}>
          <span className={className}>{icon}</span>
          {label}
        </NavItem>
        {/* Open only while one of its screens is */}
        {active &&
          children.map(child => (
            <NavItem
              key={child.key}
              type="button"
              onClick={child.onClick}
              $active={child.active}
              $sub
              aria-current={child.active ? "page" : undefined}>
              {child.label}
            </NavItem>
          ))}
      </div>
    ) : (
      <NavItem key={key} type="button" onClick={onClick} $active={active} aria-current={active ? "page" : undefined}>
        {renderIcon(icon, dot, className)}
        {label}
        {renderNewContent(dot)}
      </NavItem>
    );

  // A row of the "More" panel: closes it, then goes
  const renderMoreItem = ({ key, label, icon, onClick, active, dot }: NavEntry) => (
    <NavItem
      key={key}
      type="button"
      onClick={() => {
        setMoreOpen(false);
        onClick();
      }}
      $active={active}
      aria-current={active ? "page" : undefined}>
      {renderIcon(icon, dot)}
      {label}
      {renderNewContent(dot)}
    </NavItem>
  );

  return (
    <PreviewSiteContext.Provider value={previewSiteContext}>
      <Shell $withBottomNav={withBottomNav} $withPreview={withSidePreview}>
        <ToastProvider>
          <Sidebar>
            <SidebarHeader>
              <Logo type="button" onClick={() => navigate("/sites")} aria-label={t("components.appShell.nav.keepos")}>
                <SymbolKeepoIcon width={77} />
              </Logo>
              {userName && (
                // First name only, so it fits beside the logo; if it still doesn't, the name is cut, never the 👋
                <Greeting title={userName}>
                  <span className="name">
                    {t("pages.sites.intro.hello")}, {userName.trim().split(/\s+/)[0]}
                  </span>
                  👋
                </Greeting>
              )}
            </SidebarHeader>

            <SidebarBody>
              <NavGroup>{[keeposGroupEntry, domainsEntry].map(renderNavItem)}</NavGroup>

              {/* What a Keepo can do beyond its page (the business card, and what comes next) */}
              {renderNavGroup("tools", t("components.appShell.nav.tools"), toolEntries)}

              {/* Same look as the PLUS hero on the subscription page */}
              <SidebarPlusPromo>
                <PlusPromo onClick={plusEntry.onClick} />
              </SidebarPlusPromo>
            </SidebarBody>

            <SidebarFooter>
              <NavGroup>{footerEntries.map(renderNavItem)}</NavGroup>
              <MadeInBrazil>
                {t("components.appShell.madeInBrazil")}
                <BrazilFlag />
              </MadeInBrazil>
            </SidebarFooter>
          </Sidebar>

          <Main>{children}</Main>

          {/* Below the side-preview breakpoint, a floating button opens the preview instead (not on the
            first creation steps, where there's little to see yet, nor on the PLUS and payment screens) */}
          {withPreview && !CREATION_ROUTES_WITHOUT_SIDE_PREVIEW.includes(pathname) && !isSubscriptionInFlow && (
            <MobilePreview site={previewPage} hint={previewHint} address={shownAddress} onOpenPage={openPreviewPage} />
          )}

          {withSidePreview && (
            <PreviewAside $arriving={isPreviewArriving}>
              <div className="inner">
                <LivePreview
                  site={previewPage}
                  loading={isPreviewLoading}
                  hint={previewHint}
                  address={shownAddress}
                  onOpenPage={openPreviewPage}
                />
              </div>
            </PreviewAside>
          )}

          {withBottomNav && (
            <BottomNav>
              {bottomEntries.map(({ key, label, shortLabel, icon, onClick, active }) => (
                <BottomNavItem
                  key={key}
                  type="button"
                  onClick={onClick}
                  $active={active}
                  aria-label={label}
                  aria-current={active ? "page" : undefined}>
                  <span className="icon">{icon}</span>
                  <span className="label">{shortLabel || label}</span>
                </BottomNavItem>
              ))}
              <BottomNavItem
                ref={moreButton}
                type="button"
                onClick={() => setMoreOpen(open => !open)}
                $active={moreActive}
                aria-label={t("components.appShell.nav.short.more") + (moreHasNew ? newContent : "")}
                aria-expanded={moreOpen}
                aria-controls={moreOpen ? "more-panel" : undefined}
                aria-current={moreActive ? "page" : undefined}>
                <span className="icon relative">
                  {moreOpen ? <CloseMenuIcon /> : <MenuIcon />}
                  {moreHasNew && <NewDot aria-hidden="true" />}
                </span>
                <span className="label">{t("components.appShell.nav.short.more")}</span>
              </BottomNavItem>
            </BottomNav>
          )}

          {withBottomNav && moreOpen && (
            <>
              <BottomMoreBackdrop data-testid="more-backdrop" onClick={() => setMoreOpen(false)} />
              <BottomMoreSheet id="more-panel" ref={moreSheet} aria-label={t("components.appShell.nav.moreTitle")}>
                {userName && (
                  <BottomMoreHeader
                    type="button"
                    onClick={() => {
                      setMoreOpen(false);
                      accountEntry.onClick();
                    }}>
                    <span className="name">
                      {t("pages.sites.intro.hello")}, {userName.trim().split(/\s+/)[0]}
                    </span>
                    👋
                  </BottomMoreHeader>
                )}
                <NavGroup as="div" role="group" aria-labelledby="more-tools">
                  <NavGroupTitle id="more-tools">{t("components.appShell.nav.tools")}</NavGroupTitle>
                  {toolEntries.map(renderMoreItem)}
                </NavGroup>
                <BottomMoreDivider />
                {footerEntries.map(renderMoreItem)}
              </BottomMoreSheet>
            </>
          )}

          {/* A launch not told yet: on the main screens only, never inside a Keepo (its dashboard, editor, analytics,
              settings) nor in the middle of creating one */}
          {withBottomNav && !insideKeepo && releases.announce && !isActive("/novidades") && (
            <ReleaseAnnouncement
              release={releases.announce}
              onClose={() => releasesStatus.announced()}
              onOpen={() => {
                releasesStatus.announced();
                navigate("/novidades");
              }}
            />
          )}
        </ToastProvider>
      </Shell>
    </PreviewSiteContext.Provider>
  );
};
